import { useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { DownloadInfoItem } from '../../../../types/analytics';
import { withLoading } from '../../../common/Loading/withLoading';
import LoadingContext from '../../../../context/loadingContext/LoadingContext';
import { DatasetType } from '../../../../types/marker';
import useHomeStore from '../../../../store/homeStore';
import useLegendStore from '../../../../store/legendStore';
import { useActiveMarker, useMarkerStore } from '../../../../store/markerStore';
import useAnalyticsStore from '../../../../store/analyticsStore';
import { IWmsProps } from '../../Map/Layer/DatasetMesh';

export const useDatasetChange = (
  item: DownloadInfoItem,
  namePanel: string,
  downloadItems: DownloadInfoItem[],
  onMeshControl: (value: string) => any,
  setMeshUrl: (item?: IWmsProps) => void
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const loadingContext = useContext(LoadingContext);

  const setIsChangeDatasetWarningVisible = useHomeStore(
    (state) => state.setIsChangeDatasetWarningVisible
  );
  const setTemporaryDatasetName = useHomeStore(
    (state) => state.setTemporaryDatasetName
  );
  const temporaryDatasetName = useHomeStore(
    (state) => state.temporaryDatasetName
  );
  const isChangeDatasetWarningVisible = useHomeStore(
    (state) => state.isChangeDatasetWarningVisible
  );
  const showMesh = useLegendStore((state) => state.showMesh);

  const setDownloadPage = useMarkerStore((state) => state.setDownloadPage);
  const setActivePanel = useMarkerStore((state) => state.setActivePanel);
  const point = useActiveMarker();
  const updateMarker = useMarkerStore((state) => state.updateMarker);
  const setActiveDatasetKey = useMarkerStore(
    (state) => state.setActiveDatasetKey
  );

  const enrichedMetadata = useAnalyticsStore((state) => state.enrichedMetadata);

  const changeDataset = useCallback(
    (newDataset: string) => {
      const findActiveDownload =
        point?.downloadPage.find(
          (item) => item.radioKey === point?.activeDatasetKey
        ) ?? item;

      const currentItem = {
        datasetId: findActiveDownload.datasetId,
        dateRange: findActiveDownload.dateRange,
        radioKey: findActiveDownload.radioKey,
        options: findActiveDownload.options,
      };

      downloadItems.forEach((element) => {
        const findDataset = element.options.find((dataset) => {
          return dataset.id === newDataset;
        });

        if (findDataset) {
          currentItem.radioKey = element.radioKey;
          currentItem.options = element.options;
          currentItem.dateRange = element.dateRange;
        }
      });

      handleChangePanelCategory(currentItem.radioKey);
      const obj = Object.assign({}, currentItem, { datasetId: newDataset });

      onDatasetValueChange(obj, false);

      if (point) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set(currentItem.radioKey, newDataset);
        navigate(`/?${searchParams.toString()}`);

        updateMarker(point.id, { dataset: newDataset });
        if (showMesh) {
          onMeshControl(newDataset);
        }
      }
    },
    [
      item,
      downloadItems,
      point,
      navigate,
      location,
      showMesh,
      onMeshControl,
      updateMarker,
    ]
  );

  const onDatasetChange = useCallback(
    (value: string) => {
      const datasetTask = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === item.datasetId &&
          task.CoordinatesString === point?.coordinates.join()
      );

      if (datasetTask === undefined) {
        withLoading(() => changeDataset(value), loadingContext, {
          source: 'useDatasetChange - changeDataset',
        });
      } else {
        setTemporaryDatasetName(value);
        setIsChangeDatasetWarningVisible(true);
      }
    },
    [
      item.datasetId,
      point,
      enrichedMetadata,
      changeDataset,
      loadingContext,
      setTemporaryDatasetName,
      setIsChangeDatasetWarningVisible,
    ]
  );

  const handleChangeDatasetWarningOk = useCallback(() => {
    setIsChangeDatasetWarningVisible(false);
    withLoading(
      () => changeDataset(temporaryDatasetName ?? ''),
      loadingContext,
      { source: 'useDatasetChange - handleChangeDatasetWarningOk' }
    );
    setTemporaryDatasetName('');
  }, [
    temporaryDatasetName,
    setIsChangeDatasetWarningVisible,
    changeDataset,
    loadingContext,
    setTemporaryDatasetName,
  ]);

  const handleChangeDatasetWarningCancel = useCallback(() => {
    setIsChangeDatasetWarningVisible(false);
    setTemporaryDatasetName('');
  }, [setIsChangeDatasetWarningVisible, setTemporaryDatasetName]);

  const onDatasetValueChange = useCallback(
    (item: DownloadInfoItem, defaultPanel: boolean) => {
      if (!point || !point.downloadPage) return;
      const usedPanel = defaultPanel
        ? namePanel
        : point.activeDatasetKey ?? namePanel;
      setDownloadPage(point.id, usedPanel, item);
    },
    [point, updateMarker, point?.activeDatasetKey]
  );

  const onDateChange = useCallback(
    (value: any, defaultPanel: boolean) => {
      if (!item || !value) return;
      const obj = Object.assign({}, item, { dateRange: value });
      onDatasetValueChange(obj, defaultPanel);
    },
    [item, onDatasetValueChange]
  );

  const handleChangePanelCategory = (
    item?: DatasetType | string | string[]
  ) => {
    const itemString = Array.isArray(item) ? item[0] : item;
    if (point) {
      if (!itemString || itemString === '') {
        setActivePanel(point.id, '');
      } else {
        setActivePanel(point.id, itemString);
        setMeshUrl(undefined);
        const newDatasetId =
          point.downloadPage.find((e) => e.radioKey === item)?.datasetId ??
          point.dataset;
        setActiveDatasetKey(point.id, item as DatasetType, newDatasetId);
      }
    }
  };

  return {
    changeDataset,
    onDatasetChange,
    handleChangeDatasetWarningOk,
    handleChangeDatasetWarningCancel,
    onDateChange,
    onDatasetValueChange,
    handleChangePanelCategory,
    isChangeDatasetWarningVisible,
  };
};
